/**
 * The console-own values the `suite` variant inherited from the pre-T-31 Berry theme, frozen.
 *
 * `legacy-chrome.tokens.json` (mirrored in `suite.ts`) is silent about the grey ramp, the
 * semantic and status colours, the type scale below the headings, the shadow ramp and the
 * section surfaces prompt 22 gave the patterns — so suite used Berry's. T-31 restyles Berry to
 * the EIS Design System; decision 2 of prompt 24 says suite must render exactly as before. These
 * are Berry's values as they stood on main at `f1552af`, copied rather than referenced so the
 * restyle cannot reach suite. Nothing here is tuned: change it only when suite itself changes.
 */
import type { StatusColor, StatusToken } from './palette';

/** Berry's cool grey scale. */
export const inheritedNeutral = {
  0: '#FFFFFF',
  50: '#F8FAFC',
  100: '#EEF2F6',
  200: '#E3E8EF',
  300: '#CDD5DF',
  400: '#9AA4B2',
  500: '#697586',
  600: '#4B5565',
  700: '#364152',
  800: '#202939',
  900: '#121926',
} as const;

export const inheritedSemantic = {
  success: '#1B7A43',
  warning: '#8F6300',
  error: '#C62828',
  info: '#1976D2',
} as const;

const terminated: StatusColor = {
  main: '#C62828',
  contrastText: '#FFFFFF',
  surface: '#FDECEA',
  border: '#EF9A9A',
};

/**
 * Berry's status table. `danger` did not exist before T-31; FAILED rendered as `terminated`, so
 * suite's `danger` is `terminated` and a failed run looks in suite as it did on main.
 */
export const inheritedStatus: Readonly<Record<StatusToken, StatusColor>> = {
  draft: { main: '#4B5565', contrastText: '#FFFFFF', surface: '#EEF2F6', border: '#CDD5DF' },
  active: { main: '#1B7A43', contrastText: '#FFFFFF', surface: '#E6F7ED', border: '#A8DCBF' },
  suspended: { main: '#8F6300', contrastText: '#FFFFFF', surface: '#FFF8E1', border: '#F0D590' },
  terminated,
  pending: { main: '#1565C0', contrastText: '#FFFFFF', surface: '#E3F2FD', border: '#90CAF9' },
  published: { main: '#5E35B1', contrastText: '#FFFFFF', surface: '#EDE7F6', border: '#B39DDB' },
  danger: terminated,
};

/** Berry's radii: 8 for controls, 12 for surfaces. */
export const inheritedRadius = { sm: 6, md: 8, lg: 12, pill: 999 } as const;

/** Berry's shadow ink — a cool grey, as RGB channels. */
export const inheritedShadowInk = '18 25 38';

const ink = (alpha: number): string => `rgb(var(--eis-shadow-ink) / ${String(alpha)})`;

export const inheritedShadows: readonly string[] = [
  'none',
  `0 1px 4px 0 ${ink(0.08)}`,
  `0 2px 8px 0 ${ink(0.08)}`,
  `0 2px 14px 0 ${ink(0.1)}`,
  `0 4px 20px 0 ${ink(0.12)}`,
  ...Array.from({ length: 20 }, () => `0 8px 28px 0 ${ink(0.14)}`),
];

export const inheritedElevation = {
  card: `0 2px 14px 0 ${ink(0.08)}`,
} as const;

/** Berry's type scale — suite overrides the headings and body, and keeps the rest. */
export const inheritedTypography = {
  fontFamily:
    '"Inter", "Roboto", "Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif',
  htmlFontSize: 16,
  fontSize: 14,
  h1: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.01em' },
  h2: { fontSize: '1.375rem', fontWeight: 700, letterSpacing: '-0.01em' },
  h3: { fontSize: '1.125rem', fontWeight: 600 },
  h4: { fontSize: '1rem', fontWeight: 600 },
  h5: { fontSize: '0.9375rem', fontWeight: 600 },
  h6: { fontSize: '0.875rem', fontWeight: 600 },
  subtitle1: { fontSize: '0.875rem', fontWeight: 600 },
  subtitle2: { fontSize: '0.8125rem', fontWeight: 600 },
  body1: { fontSize: '0.875rem' },
  body2: { fontSize: '0.8125rem' },
  button: { fontSize: '0.8125rem', fontWeight: 600, textTransform: 'none' },
  caption: { fontSize: '0.75rem' },
  overline: { fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em' },
} as const;
